import React, {useCallback, useContext, useMemo, useState} from "react";
import {Opening} from "../../../../api/Openings";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {Button, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {PrepManager} from "../../shopdrawing/hardware/prepCanvas/PrepManager";
import {useHwAPI} from "../../shopdrawing/hardware/HardwareGeneric";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {first, useAsync2} from "nate-react-api-helpers";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {hasCustomPrep} from "./QuoteOpenings";
import {red} from "@mui/material/colors";
import {makeNumberOrUndefined} from "./util";

type PromiseCallback<T> = {
    resolve: (value: T) => void
    reject: (err: Error) => void;
}

export function PrepLocations<T extends (Opening | QuoteOpening)>(props: {
    value: T;
    onDone(value: T): void;
    onCancel(): void;
}) {
    const hw = useHwAPI();
    const [shouldUpdateExistingPromise, setShouldUpdateExistingPromise] = useState<PromiseCallback<boolean>&{
        affects: string[]
    }|null>(null);

    const shouldWeUpdateExisting = useCallback((affects: string[]) => {
        return new Promise<boolean>((resolve, reject) => setShouldUpdateExistingPromise({
            affects: affects,
            resolve: resolve,
            reject: reject,
        }));
    }, [])

    const edit = useContext(EditContext);

    const hardware = useAsync2(() => {
        return hw.listWithContents({
            groups: props.value.hardwareGroup ? [props.value.hardwareGroup] : [],
            project: props.value.project,
        })
    }, {}, []);

    const hardwareItems = first(hardware.result || [], v => true)?.hardware

    const show = useMemo(() => {
        return {
            id: props.value.hardwareGroup || 0,
            name: props.value.hardwareGroupName || "",
            project: props.value.project,
            openings: [props.value as any],
            hardware: hardwareItems || [],
            prepConfigJson: props.value.hardwareGroupPrepConfig || [],
            flipDoubleActiveHanding: false,
            notes: props.value.notes,
        };
    }, [props.value, hardwareItems]);

    const editRef = useSyncedRef(edit);
    const onDoneRef = useSyncedRef(props.onDone)
    const onDone = useCallback((input: T) => {
        onDoneRef.current(input);
        editRef.current.onSelect.emit(null)
        editRef.current.reload();
    }, [onDoneRef, editRef]);

    if(hardware.loadingOrError) {
        return (
            <Dialog2 open onClose={() => {
                        shouldUpdateExistingPromise?.reject(new Error("User Cancelled"));
                        setShouldUpdateExistingPromise(null);
                    }}>
                <DialogTitle>Loading Hardware</DialogTitle>
                <DialogContent>
                    {hardware.LoadingOrErrorElement}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        shouldUpdateExistingPromise?.reject(new Error("User Cancelled"));
                        setShouldUpdateExistingPromise(null);
                    }}>Cancel</Button>
                </DialogActions>
            </Dialog2>
        )
    }

    return (
        <>
            {shouldUpdateExistingPromise && <Dialog2 open onClose={() => {
                        shouldUpdateExistingPromise?.reject(new Error("User Cancelled"));
                        setShouldUpdateExistingPromise(null);
                    }} onEnterPress={() => {
                        shouldUpdateExistingPromise?.resolve(true);
                        setShouldUpdateExistingPromise(null);
                    }}>
                <DialogTitle>Update Existing Hardware Heading?</DialogTitle>
                <DialogContent>
                    {shouldUpdateExistingPromise.affects.length > 0 ?
                        <div>Would you like to update the existing hardware heading (<span style={{color: red["800"]}}>and {addS("opening", shouldUpdateExistingPromise.affects.length > 1)} {shouldUpdateExistingPromise.affects.join(", ")}</span>), or create a new one?</div> :
                        <div>Would you like to update the existing hardware heading (no other openings affected), or create a new one?</div>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        shouldUpdateExistingPromise?.reject(new Error("User Cancelled"));
                        setShouldUpdateExistingPromise(null);
                    }}>Cancel</Button>
                    <Button onClick={() => {
                        shouldUpdateExistingPromise?.resolve(true);
                        setShouldUpdateExistingPromise(null);
                    }}>Update Existing</Button>
                    <Button onClick={() => {
                        shouldUpdateExistingPromise?.resolve(false);
                        setShouldUpdateExistingPromise(null);
                    }}>Create New</Button>
                </DialogActions>
            </Dialog2>}
            <PrepManager
                value={show}
                  onCancel={props.onCancel}
                  onDone={async cfg => {
                      if(!props.value.hardwareGroup) {

                          const rs = await hw.upsert({
                                value: {
                                    id: 0,
                                    project: props.value.project,
                                    openings: [props.value as any],
                                    hardware: [],
                                    prepConfigJson: cfg,
                                    prepConfigIsCustom: hasCustomPrep(cfg),
                                    flipDoubleActiveHanding: props.value.flipDoubleActiveHanding,
                                    notes: "",

                                    // @ts-ignore
                                    quoteAlternative: props.value.quoteAlternative,
                                }
                            });

                          if(!rs.updated) throw new Error("Failed to update hardware heading")

                          onDone(Object.assign({}, props.value, {
                              hardwareGroup: rs.id,
                              hardwareGroupName: rs.name,

                          }));
                          return;
                      }


                      const list = await hw.listWithContents({
                          groups: [props.value.hardwareGroup as number],
                          project: props.value.project,
                      });

                      const merge = await shouldWeUpdateExisting(
                          list[0].openings.map(v => v.name).filter(v => v !== props.value.name)
                      );

                      const value = list[0]
                      const withShortCode = first(value.hardware, h => !!h.shortcode)

                      if(merge) {

                          const rs = await hw.upsert({
                              linkToShortcode: makeNumberOrUndefined(withShortCode?.shortcode || undefined),
                              value: Object.assign({}, value, {
                                  prepConfigJson: cfg,
                                  prepConfigIsCustom: hasCustomPrep(cfg),
                              })
                          });

                          if(!rs.updated) throw new Error("Failed to update hardware heading")
                          onDone(Object.assign({}, props.value, {
                              hardwareGroup: rs.id,
                              hardwareGroupName: rs.name,
                          }));
                          return;
                      }

                      const rs = await hw.upsert({
                          value: Object.assign({}, value, {
                              id: 0,
                              name: "",
                              openings: [props.value as any],
                              prepConfigJson: cfg,
                              prepConfigIsCustom: hasCustomPrep(cfg),
                          }),
                          linkToShortcode: makeNumberOrUndefined(withShortCode?.shortcode || undefined),
                      });

                      if(!rs.updated) throw new Error("Failed to update hardware group")
                      onDone(Object.assign({}, props.value, {
                          hardwareGroup: rs.id,
                          hardwareGroupName: rs.name,
                      }));
                  }} />
        </>
    )
}

function addS(src: string, add: boolean) {
    if(add) {
        return src + "s";
    }

    return src;
}